import { t } from "@lingui/macro";
import { Button } from "@winamp/ui/components/Button/Button.js";
import type { ComponentProps } from "react";
import { useLocation, useNavigate } from "react-router";
import { ServiceEnum } from "~/models/service";
import { getIsServiceActive } from "~/models/subscription";
import { useAppContext, useAuth } from "~/modules/auth/hooks";
import { getDialogRoute } from "~/modules/route-dialogs/RouteDialogsContext";

type ServiceSubmitButtonProps = ComponentProps<typeof Button> & {
  enableMetadataWarning?: boolean;
  isSubmitting: boolean;
  subscribeLabel?: string;
  kycLabel?: string;
  requiresKyc?: boolean;
  requiresSubscription?: boolean;
  serviceId: ServiceEnum;
} & (
    | {
        requiresActivation?: true;
        activationLabel?: string;
        isServiceActivated: boolean;
      }
    | {
        requiresActivation: false;
        activationLabel?: never;
        isServiceActivated?: never;
      }
  );

export function useServiceSubmission({
  activationLabel,
  enableMetadataWarning = false,
  isServiceActivated,
  kycLabel,
  requiresActivation = true,
  requiresKyc = true,
  requiresSubscription = true,
  serviceId,
  subscribeLabel,
  ...submitButtonProps
}: Omit<ServiceSubmitButtonProps, "isSubmitting" | "children">) {
  const { user } = useAuth();
  const location = useLocation();
  const context = useAppContext();
  const navigate = useNavigate();
  const hasConnectedWallet = !!user.wallet.crypto.address;

  const onClick = (e: React.MouseEvent) => {
    if (
      requiresSubscription &&
      !getIsServiceActive(serviceId, context.subscription)
    ) {
      e.preventDefault();
      navigate(
        getDialogRoute("subscribe", {
          checkoutRedirection: [location.pathname, location.search].join(""),
          artistUid: context.artist?.uid ?? "",
          labelUid: context.label?.uid ?? "",
        }),
        { preventScrollReset: true },
      );
      return;
    }

    if (requiresKyc && ["Declined", "Unverified"].includes(user.kyc.status)) {
      e.preventDefault();
      navigate(getDialogRoute("kyc.start"), {
        preventScrollReset: true,
      });
      return;
    }

    if (requiresActivation === true && !isServiceActivated) {
      e.preventDefault();
      navigate(
        getDialogRoute(
          serviceId === ServiceEnum.Rights
            ? "service.activate.rights"
            : "service.activate",
          {
            serviceId: serviceId.toString(),
            artistUid: context.artist?.uid ?? "",
          },
        ),
        { preventScrollReset: true },
      );
      return;
    }

    if (serviceId === ServiceEnum.Nft && !hasConnectedWallet) {
      e.preventDefault();
      navigate(getDialogRoute("profile.wallet"), { preventScrollReset: true });
      return;
    }

    if (
      user.preferences.metadataWarningDialog &&
      enableMetadataWarning &&
      !("to" in submitButtonProps)
    ) {
      e.preventDefault();
      const props = submitButtonProps as ComponentProps<"button">;
      navigate(
        getDialogRoute("audio.publish.disclaimer", {
          form: props.form ?? "",
          name: props.name,
          value: typeof props.value === "string" ? props.value : undefined,
        }),
        {
          preventScrollReset: true,
        },
      );
      return;
    }
  };

  const getSubmissionLabel = () => {
    if (
      requiresSubscription &&
      !getIsServiceActive(serviceId, context.subscription)
    ) {
      return subscribeLabel ?? t`Subscribe & submit`;
    }

    if (requiresKyc && ["Declined", "Unverified"].includes(user.kyc.status)) {
      return kycLabel ?? t`Verify your identity & submit`;
    }

    if (requiresActivation && !isServiceActivated) {
      return activationLabel ?? t`Activate & submit`;
    }

    if (serviceId === ServiceEnum.Nft && !hasConnectedWallet) {
      return t`Connect Wallet & submit`;
    }
  };

  return {
    onClick,
    submissionLabel: getSubmissionLabel(),
  };
}

export function ServiceSubmitButton({
  activationLabel,
  children,
  enableMetadataWarning = false,
  isServiceActivated,
  isSubmitting,
  kycLabel,
  requiresActivation = true,
  requiresKyc = true,
  requiresSubscription = true,
  subscribeLabel,
  serviceId,
  ...props
}: ServiceSubmitButtonProps) {
  const { onClick, submissionLabel } = useServiceSubmission({
    activationLabel,
    enableMetadataWarning,
    isServiceActivated,
    kycLabel,
    requiresActivation,
    requiresKyc,
    requiresSubscription,
    subscribeLabel,
    serviceId,
    ...props,
  });

  return (
    <Button
      {...props}
      type="submit"
      loading={isSubmitting}
      onClick={(e: React.MouseEvent<HTMLElement, MouseEvent>) => onClick(e)}
    >
      {submissionLabel ?? children}
    </Button>
  );
}
